export const ORDAN_PRIVACY_POLICY_EFFECTIVE_DATE = 'June 6, 2026';
export const ORDAN_PRIVACY_POLICY_VERSION = '1.0';

export interface PrivacyPolicySection {
  title: string;
  paragraphs: string[];
}

export const ORDANPrivacyPolicySections: PrivacyPolicySection[] = [
  {
    title: '1. Introduction',
    paragraphs: [
      'This Privacy Policy describes how ORDAN ("we", "us", or "our") collects, uses, stores, and protects information when you use the ORDAN Mobile Workplace application ("the App") on iOS, Android, or other supported devices.',
      'The App is a business-to-business (B2B) enterprise resource planning (ERP) companion. It is intended for employees, contractors, and authorized users of organizations that subscribe to ORDAN. You must use credentials issued by your employer or organization to sign in.',
      'The App is not directed at children under 13 (or the minimum age required in your jurisdiction). We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '2. Who We Are',
    paragraphs: [
      'ORDAN operates the ORDAN Mobile Workplace application and the backend services that power it, including the API hosted at ehome.ntesbusiness.com.',
      'When you use the App, you access your organization\'s ERP tenant. Your employer or organization may also act as a data controller for business records, operational data, and employment-related information stored in the ERP system.',
      'If you have questions about how your organization handles your employment data, contact your employer\'s administrator in addition to the contact details in Section 15.',
    ],
  },
  {
    title: '3. Information You Provide',
    paragraphs: [
      'Account credentials: When you sign in, you enter a username or email address and password. Your password is transmitted securely to our servers for authentication and is not stored in plain text on your device.',
      'Workflow and business data: As you perform job functions in the App, you may enter or submit text such as remarks, comments, inspection findings, customer or witness names, asset condition notes, and other operational information required by your role.',
      'Photos and signatures: You may capture or attach photographs (for example, proof-of-delivery images, asset verification photos, task attachments, or quality inspection images) and digital signatures where the App supports those features.',
      'Barcodes: When you scan barcodes using the device camera, the scanned code value is sent to the ERP backend for lookup. The App does not upload barcode preview images.',
    ],
  },
  {
    title: '4. Information Collected Automatically',
    paragraphs: [
      'Device identification: The App generates and stores a device identifier locally and registers your device with the backend when you are signed in. Registration includes a device token, device type (such as Android or iOS), application version, and a device name derived from the platform.',
      'Session tokens: After successful authentication, access and refresh tokens are stored locally on your device to maintain your session and authorize API requests.',
      'Location data: When you use features that support location capture (such as proof of delivery or asset verification), the App may request your device\'s GPS coordinates with your permission. Location may be embedded in record descriptions or metadata submitted to your organization\'s ERP. The App does not collect continuous background location.',
      'Network status: The App checks network connectivity locally to determine online or offline mode. This status is not transmitted to third-party analytics services.',
      'AI assistant (if enabled): If your organization has enabled the AI assistant, the App may store a local session identifier and transmit chat messages and context to the backend to provide assistance.',
    ],
  },
  {
    title: '5. Device Permissions',
    paragraphs: [
      'Camera: Used to capture proof-of-delivery photos, asset verification images, task attachments, quality inspection attachments, and live barcode scanning. You can deny camera access; features that require photos or scanning will not function without it.',
      'Photo library: Used to attach existing photos for proof of delivery. You can deny photo library access; you will need to use the camera instead or skip photo attachments.',
      'Location (when in use): Used to optionally record GPS coordinates for proof of delivery and asset-related workflows. You can deny location access or skip location capture when prompted.',
      'Biometrics or device PIN: If you enable optional biometric unlock in security settings, your device\'s operating system handles fingerprint, face recognition, or PIN verification locally. Biometric templates are not collected, stored, or transmitted by ORDAN. We store only your preference to use biometric unlock.',
    ],
  },
  {
    title: '6. How We Use Your Information',
    paragraphs: [
      'Authenticate you and maintain your session with your organization\'s ERP tenant.',
      'Provide role-based features you are authorized to use, including approvals, deliveries, notifications, shop-floor operations, quality inspections, asset management, tasks, analytics dashboards, and barcode lookups.',
      'Process and store proof-of-delivery records, including photos, signatures, and delivery metadata, on your organization\'s systems.',
      'Deliver in-app notifications by synchronizing with the backend. Native push notifications (FCM/APNs) may be introduced in future versions; device registration data supports that capability.',
      'Provide optional AI-assisted features when enabled by your organization\'s administrator.',
      'Maintain security, troubleshoot issues, and improve reliability of the App and backend services.',
    ],
  },
  {
    title: '7. Local Storage on Your Device',
    paragraphs: [
      'The App stores data locally using platform storage mechanisms (SharedPreferences on supported platforms), including:',
      'Authentication tokens (access token and refresh token) to keep you signed in.',
      'Your preference for biometric unlock.',
      'A generated device identifier and related registration metadata.',
      'An offline queue of pending actions when network connectivity is unavailable. This queue may temporarily contain encoded photos, signatures, and other submission data until it syncs to the server.',
      'Downloaded documents may be written to a temporary directory on your device for viewing through the system document viewer.',
    ],
  },
  {
    title: '8. Third-Party Services',
    paragraphs: [
      'Employer ERP backend: All authenticated App data is transmitted to and stored on servers operated for your organization\'s ORDAN ERP deployment (for example, ehome.ntesbusiness.com).',
      'Google Fonts: The App may download font files from Google\'s servers at runtime to display typography. No personal data is intentionally sent to Google as part of font loading, but Google may receive standard network request metadata (such as IP address).',
      'Operating system services: Biometric authentication, camera, location, and photo library access are provided by your device\'s operating system (Apple iOS or Google Android) under their respective privacy policies.',
      'We do not integrate third-party advertising networks, behavioral analytics SDKs, or crash reporting services in the mobile App as currently built.',
    ],
  },
  {
    title: '9. Data Sharing and Sale',
    paragraphs: [
      'We do not sell your personal information.',
      'We do not share your personal information with third parties for their own marketing purposes.',
      'Information you submit through the App is shared with your organization\'s ERP backend and is accessible to authorized users within your organization according to role-based access controls configured by your employer.',
      'We may disclose information if required by law, regulation, legal process, or governmental request, or to protect the rights, property, or safety of ORDAN, our users, or others.',
    ],
  },
  {
    title: '10. Data Retention and Deletion',
    paragraphs: [
      'Business and operational data retained in the ERP system is governed by your organization\'s data retention policies and applicable law.',
      'When you sign out of the App, session tokens are cleared from local storage and your device registration for notifications is deactivated on the backend.',
      'Offline queued data is removed from local storage after successful synchronization.',
      'To request deletion or correction of account or employment data, contact your organization\'s administrator. ORDAN processes such requests in coordination with the data controller (your employer) where applicable.',
    ],
  },
  {
    title: '11. Security',
    paragraphs: [
      'Data transmitted between the App and the production backend is encrypted in transit using HTTPS (TLS).',
      'Access to ERP data is protected by authentication tokens and server-side role-based access controls.',
      'Session tokens are stored in application local storage. For enhanced protection, future versions may migrate sensitive credentials to platform secure storage (such as the iOS Keychain or Android Keystore).',
      'No method of transmission or storage is completely secure. We encourage you to use device lock screens, keep your operating system updated, and protect your login credentials.',
    ],
  },
  {
    title: '12. Your Choices and Rights',
    paragraphs: [
      'You may choose not to use the App; without signing in, core App functionality is unavailable.',
      'You can grant or revoke camera, photo library, and location permissions through your device settings at any time.',
      'Biometric unlock is optional and can be disabled in the App\'s security settings.',
      'Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict processing of your personal information. Submit requests through your organization\'s administrator or contact us using the details in Section 15.',
    ],
  },
  {
    title: '13. International Users',
    paragraphs: [
      'The App connects to ERP servers designated by your organization. Data may be processed and stored in the country or region where those servers are hosted.',
      'If you use the App from outside that region, your information may be transferred to, stored, and processed in the server location. Your organization is responsible for ensuring appropriate legal bases and safeguards for such transfers where required.',
    ],
  },
  {
    title: '14. Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time to reflect changes in the App, legal requirements, or our practices.',
      'The effective date at the top of this policy indicates when it was last revised. Continued use of the App after changes are posted constitutes acceptance of the updated policy where permitted by law.',
      'Material changes may also be communicated through the App, your organization\'s administrator, or other appropriate channels.',
    ],
  },
  {
    title: '15. Contact Us',
    paragraphs: [
      'If you have questions or concerns about this Privacy Policy or ORDAN\'s handling of information in the Mobile Workplace App, contact us at:',
      'Email: privacy@ordan.com',
      'Product: ORDAN Mobile Workplace',
      'Web: https://ehome.ntesbusiness.com/privacy',
      'For account access, permissions, or data held by your employer in the ERP system, please also contact your organization\'s system administrator.',
    ],
  },
];
